import { c as _c } from "react/compiler-runtime";
import { ArrowDownToLine, ArrowUpRight, Download, HandCoins, Tag, Wallet } from "lucide-react";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { balance, offerings, payouts, positions } from "@/mocks/residual";
import { formatDate, formatNumber, formatUsd, truncateAddress } from "@/lib/format";
export function PortfolioPage({
  onListForSale,
  onViewOffering
}: {
  onListForSale: (positionId: string) => void;
  onViewOffering: (offeringId: string) => void;
}) {
  const [tab, setTab] = useState<"positions" | "wallet">("positions");
  const totals = useMemo(() => {
    const value = positions.reduce((s, p) => s + p.currentValueUsd, 0);
    const cost = positions.reduce((s_0, p_0) => s_0 + p_0.tokens * p_0.avgUnitPriceUsd, 0);
    const income = positions.reduce((s_1, p_1) => s_1 + p_1.totalIncomeUsd, 0);
    const lifetime = payouts.reduce((s_2, p_2) => s_2 + p_2.amountUsd, 0);
    return {
      value,
      cost,
      income,
      lifetime,
      pnl: value - cost
    };
  }, []);
  return <div className="space-y-5">
			<div>
				<h1 className="text-2xl font-semibold tracking-tight">Portfolio</h1>
				<p className="text-sm text-muted-foreground">
					Your positions, balances, and trading wallet.
				</p>
			</div>

			<Card className="overflow-hidden">
				<CardContent className="space-y-4 pt-6">
					<div>
						<div className="text-xs uppercase tracking-wide text-muted-foreground">
							Portfolio value
						</div>
						<div className="text-3xl font-semibold tabular-nums">
							{formatUsd(totals.value)}
						</div>
						<div className="mt-1 flex items-center gap-2 text-sm">
							<span className={totals.pnl >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}>
								{totals.pnl >= 0 ? "+" : ""}
								{formatUsd(totals.pnl)}
							</span>
							<span className="text-muted-foreground">all-time</span>
						</div>
					</div>
					<Separator />
					<div className="grid grid-cols-3 gap-3 text-center">
						<MiniStat label="Cost" value={formatUsd(totals.cost)} />
						<MiniStat label="Income" value={formatUsd(totals.lifetime)} />
						<MiniStat label="Positions" value={formatNumber(positions.length)} />
					</div>
				</CardContent>
			</Card>

			<div className="grid grid-cols-2 gap-2">
				<Tab active={tab === "positions"} onClick={() => setTab("positions")} label={`Positions (${positions.length})`} />
				<Tab active={tab === "wallet"} onClick={() => setTab("wallet")} label="Wallet & balance" />
			</div>

			{tab === "positions" && <div className="space-y-3">
					{positions.length === 0 ? <EmptyState title="No positions yet" body="Buy from any active offering to start receiving income." /> : positions.map(p_3 => {
        const o = offerings.find(x => x.id === p_3.offeringId);
        if (!o) return null;
        const pnl = p_3.currentValueUsd - p_3.tokens * p_3.avgUnitPriceUsd;
        return <Card key={p_3.id}>
									<CardContent className="space-y-3 pt-5">
										<button type="button" onClick={() => onViewOffering(o.id)} className="flex w-full items-start gap-3 text-left">
											<div className="size-12 shrink-0 rounded-lg bg-cover bg-center" style={{
                backgroundImage: `url(${o.heroUrl})`
              }} />
											<div className="flex-1">
												<div className="text-sm font-semibold leading-tight">
													{o.title}
												</div>
												<div className="text-xs text-muted-foreground">
													{formatNumber(p_3.tokens)} {o.tokenSymbol} •
													bought {formatDate(p_3.purchasedAt)}
												</div>
											</div>
											<div className="text-right">
												<div className="text-sm font-semibold tabular-nums">
													{formatUsd(p_3.currentValueUsd)}
												</div>
												<div className={`text-xs ${pnl >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
													{pnl >= 0 ? "+" : ""}
													{formatUsd(pnl)}
												</div>
											</div>
										</button>

										<Separator />

										<div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
											<div className="flex items-center gap-1.5">
												<HandCoins className="size-3.5" /> Income:{" "}
												<span className="font-medium text-foreground">
													{formatUsd(p_3.totalIncomeUsd)}
												</span>
											</div>
											<div className="flex gap-2">
												{p_3.certificateUrl && <Button size="sm" variant="outline" className="h-8 gap-1.5" asChild>
														<a href={p_3.certificateUrl}>
															<Download className="size-3.5" />
															Certificate
														</a>
													</Button>}
												<Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => onListForSale(p_3.id)}>
													<Tag className="size-3.5" />
													Sell
												</Button>
											</div>
										</div>
									</CardContent>
								</Card>;
      })}
				</div>}

			{tab === "wallet" && <div className="space-y-3">
					<Card>
						<CardContent className="space-y-4 pt-6">
							<BalanceRow icon={<HandCoins className="size-4" />} label="Platform balance" value={formatUsd(balance.platformUsd)} caption="Used for secondary trades and incoming payouts" />
							<Separator />
							<BalanceRow icon={<ArrowUpRight className="size-4" />} label="Referral balance" value={formatUsd(balance.referralUsd)} caption="Withdrawable or reinvestable" />
						</CardContent>
					</Card>

					<div className="grid grid-cols-2 gap-2">
						<Button variant="outline" className="gap-1.5">
							<ArrowDownToLine className="size-4" /> Deposit
						</Button>
						<Button variant="outline" className="gap-1.5">
							<ArrowUpRight className="size-4" /> Withdraw
						</Button>
					</div>

					<Card>
						<CardContent className="space-y-3 pt-6">
							<div className="text-sm font-medium">Connected wallet</div>
							<div className="flex items-center gap-3">
								<div className="grid size-10 place-items-center rounded-lg bg-muted">
									<Wallet className="size-4" />
								</div>
								<div className="flex-1">
									<div className="text-sm">
										{balance.wallet.kind === "self-custody" ? "Self-custody" : "Custodial (managed)"}
									</div>
									{balance.wallet.address && <div className="font-mono text-xs text-muted-foreground">
											{truncateAddress(balance.wallet.address)} ·{" "}
											{balance.wallet.chain}
										</div>}
								</div>
								<Badge variant="outline" className="rounded-full font-normal">
									{balance.wallet.kind === "self-custody" ? "Connected" : "Auto"}
								</Badge>
							</div>
							{balance.wallet.kind === "custodial" && <p className="text-xs text-muted-foreground">
									You can transfer your tokens to an external wallet at any
									time.
								</p>}
						</CardContent>
					</Card>
				</div>}
		</div>;
}
function MiniStat(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <div className="mt-0.5 text-sm font-semibold tabular-nums">{value}</div>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div>{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function Tab(t0) {
  const $ = _c(4);
  const {
    active,
    onClick,
    label
  } = t0;
  const t1 = `rounded-lg border py-2 text-sm font-medium transition-colors ${active ? "border-foreground bg-foreground text-background" : "hover:bg-muted"}`;
  let t2;
  if ($[0] !== label || $[1] !== onClick || $[2] !== t1) {
    t2 = <button type="button" onClick={onClick} className={t1}>{label}</button>;
    $[0] = label;
    $[1] = onClick;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function BalanceRow(t0) {
  const $ = _c(15);
  const {
    icon,
    label,
    value,
    caption
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <div className="grid size-9 place-items-center rounded-lg bg-muted">{icon}</div>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <div className="text-sm">{label}</div>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== caption) {
    t3 = <div className="text-xs text-muted-foreground">{caption}</div>;
    $[4] = caption;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="flex-1">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== value) {
    t5 = <div className="text-base font-semibold tabular-nums">{value}</div>;
    $[9] = value;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t1 || $[12] !== t4 || $[13] !== t5) {
    t6 = <div className="flex items-center gap-3">{t1}{t4}{t5}</div>;
    $[11] = t1;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function EmptyState(t0) {
  const $ = _c(7);
  const {
    title,
    body
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <p className="text-sm font-medium">{title}</p>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== body) {
    t2 = <p className="mt-1 text-xs text-muted-foreground">{body}</p>;
    $[2] = body;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="rounded-2xl border border-dashed p-10 text-center">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}